import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Building2, 
  User, 
  Calendar,
  CheckCircle2,
  Download,
  ExternalLink
} from 'lucide-react';
import { facturasPendientesApi, resultadosApi } from '../../lib/api';
import { formatCurrency, formatPeriodo, cn } from '../../lib/utils';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export const AnalistaFacturasPendientes = () => {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchFacturas();
  }, []);

  const fetchFacturas = async () => {
    try {
      setLoading(true);
      const res = await facturasPendientesApi.list();
      setFacturas(res.data);
    } catch (err) {
      console.error('Error fetching facturas:', err);
      setError('Error al cargar las facturas pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleProcesar = async (resultadoId) => {
    setProcessingId(resultadoId);
    try {
      await resultadosApi.procesarFactura(resultadoId);
      toast.success('Factura marcada como procesada');
      // Remove from list
      setFacturas(prev => prev.filter(f => f.resultado_id !== resultadoId));
    } catch (err) {
      console.error('Error processing factura:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar la factura');
    } finally {
      setProcessingId(null);
    }
  };

  const formatFechaCarga = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingState message="Cargando facturas pendientes..." />;
  if (error) return <ErrorState message={error} onRetry={fetchFacturas} />;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Facturas Pendientes
        </h1>
        <p className="text-slate-500">
          Facturas cargadas por propietarios que requieren procesamiento
        </p>
      </div>

      {/* Stats */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-700">{facturas.length}</p>
            <p className="text-sm text-amber-600">facturas pendientes de procesar</p>
          </div>
        </div>
      </div>

      {/* Facturas List */}
      {facturas.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="Sin facturas pendientes"
          description="No hay facturas de propietarios pendientes de procesar."
        />
      ) : (
        <div className="space-y-4">
          {facturas.map((factura) => (
            <div
              key={factura.resultado_id}
              className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-colors"
              data-testid={`factura-${factura.resultado_id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-semibold text-slate-900">
                      {factura.unidad_nombre}
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-600">{formatPeriodo(factura.periodo)}</span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-4 h-4" />
                      <span>{factura.edificio_nombre}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User className="w-4 h-4" />
                      <span>{factura.propietario_nombre}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      <span>Cargada: {formatFechaCarga(factura.factura_propietario?.fecha_carga)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Ganancia Neta:
                    </span>
                    <span className="ml-2 font-mono font-semibold text-green-700">
                      {formatCurrency(factura.ganancia_neta?.monto, factura.ganancia_neta?.moneda)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={resultadosApi.downloadFacturaPropietario(factura.resultado_id)}
                      download
                      data-testid={`download-${factura.resultado_id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Ver Factura
                    </a>
                  </Button>
                  <Button
                    onClick={() => handleProcesar(factura.resultado_id)}
                    disabled={processingId === factura.resultado_id}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid={`procesar-${factura.resultado_id}`}
                  >
                    {processingId === factura.resultado_id ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Marcar Procesada
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalistaFacturasPendientes;
